import { Button, Card, Empty, Space, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router';

import { PATHS } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { DisabledActionTooltip, TextLink } from '@/shared/ui';

import {
  billingModel,
  RemoveProductModal,
  serviceIsVisible,
  useRemoveProductModal,
} from '@/entities/billing';
import { getBillingProductBlockReason, organizationModel } from '@/entities/organization';

import { PackageBar } from '@/features/packageBar';
import { SubscriptionSummary } from '@/features/subscriptionSummary';

import {
  addPackageBlockReasonMessages,
  removePackageBlockReasonMessages,
} from '../../config';

const { Title } = Typography;

const selectAccountInfoLoadingState =
  billingModel.selectors.getLoadingStateSelector('accountInfo');

type PackageProps = {
  organizationID: string;
};

// TODO: Remove Number constructor when in protocol fix bigint
export const Packages = (props: PackageProps) => {
  const { organizationID } = props;
  const { formatMessage } = useIntl();

  const packages = useAppSelector(billingModel.selectors.selectPackages);
  const accountInfoLoadingState = useAppSelector(selectAccountInfoLoadingState);
  const { ID: accountID } = useAppSelector(
    billingModel.selectors.selectAccountInfo,
  );
  const organization = useAppSelector(
    organizationModel.selectors.selectOrganization,
  );

  const removePackageModal = useRemoveProductModal();

  const canEditProducts = Boolean(
    organization?.YourPermissions?.BillingCanEditProducts,
  );
  const addPackagePath = generatePath(PATHS.addPackage, { organizationID });
  const addPackageBlockReason = getBillingProductBlockReason({
    canEditProducts,
  });
  const removeBlockedTooltip = addPackageBlockReason
    ? formatMessage(removePackageBlockReasonMessages[addPackageBlockReason])
    : undefined;

  const renderRemoveButton = (packageID: string) => {
    const button = (
      <Button
        key="remove"
        type="primary"
        disabled={Boolean(addPackageBlockReason)}
        onClick={() =>
          removePackageModal.openModal({ productID: packageID, accountID })
        }
      >
        <FormattedMessage
          id="currentSubscription.removeSubscription"
          defaultMessage="Remove"
        />
      </Button>
    );

    if (!removeBlockedTooltip) {
      return button;
    }

    return (
      <DisabledActionTooltip key="remove" title={removeBlockedTooltip}>
        {button}
      </DisabledActionTooltip>
    );
  };

  return (
    <Card
      style={{ height: '100%' }}
      title={
        <Title level={3} style={{ marginBottom: 0 }}>
          <FormattedMessage id="packages.title" defaultMessage="Packages" />
        </Title>
      }
      bordered={false}
      extra={
        addPackageBlockReason ? (
          <DisabledActionTooltip
            title={formatMessage(
              addPackageBlockReasonMessages[addPackageBlockReason],
            )}
          >
            <TextLink to={addPackagePath} disabled>
              <FormattedMessage id="addOns.add" defaultMessage="Add" />
            </TextLink>
          </DisabledActionTooltip>
        ) : (
          <TextLink to={addPackagePath}>
            <FormattedMessage id="addOns.add" defaultMessage="Add" />
          </TextLink>
        )
      }
      loading={accountInfoLoadingState !== 'succeeded'}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {packages.length > 0 ? (
          packages.map((packageItem) => (
            <Space
              key={packageItem.ID}
              direction="vertical"
              size="middle"
              style={{ display: 'flex' }}
            >
              <SubscriptionSummary
                productID={packageItem.ID}
                title={packageItem?.Model?.Name}
                startDate={Number(packageItem?.Start?.seconds ?? 0) * 1000}
                endDate={Number(packageItem?.End?.seconds ?? 0) * 1000}
                status={packageItem.Status}
              />

              {packageItem.Services.filter(serviceIsVisible).map((service) => (
                <PackageBar
                  key={`${packageItem.ID}-${service.Types.toString()}-${service.Kind.case}-${service.Group}-${service.Kind.value.toJsonString()}`}
                  packageItem={service}
                />
              ))}

              {renderRemoveButton(packageItem.ID)}
            </Space>
          ))
        ) : (
          <Empty
            description={
              <FormattedMessage
                id="packages.noPackages"
                defaultMessage="No packages"
              />
            }
          />
        )}
      </Space>
      <RemoveProductModal
        visible={removePackageModal.visible}
        productID={removePackageModal.productID}
        productType="package"
        accountID={removePackageModal.accountID}
        canEditProducts={canEditProducts}
        onCancel={removePackageModal.closeModal}
      />
    </Card>
  );
};
